// Shared storage helpers for per-field suggestion status.
//
// **On-disk vs in-code naming.** The localStorage key prefix is
// `rejectedSuggestions-…` (`rejectedSuggestionsDE2-…` on DE2), a legacy
// artefact from when this bucket only held rejections. We deliberately keep
// the on-disk key prefixes unchanged so existing users don't lose their
// persisted rejections; in-code symbols use `suggestionStatus*` names so the
// code describes what it does today.
//
// **Bucket shape.** New writes store `'accepted' | 'rejected'` per field.
// Legacy entries with the value `true` are tolerated on read as `'rejected'`
// (`readSuggestionStatusFromBucket` performs the coercion). Migration is
// lazy — we never rewrite the bucket purely to upgrade old values.

export type SuggestionStatus =
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'existing-content';

// What we tolerate as a stored value: the two current strings plus the
// legacy `true` form. Anything else is treated as pending.
export type StoredSuggestionStatus = 'accepted' | 'rejected' | true;

export type SuggestionStatusBucket = Record<string, StoredSuggestionStatus>;

type StorageKeyArgs = {
  reviewId: string;
  doi?: string | null;
  documentId?: number | null;
};

// Callers normally provide a DOI or a documentId — DE1's data-extraction
// context routinely carries one. The `no-document` branch is a last-resort
// fallback for the brief window before either becomes available; we don't
// guard against it propagating into reads/writes, since by then the field
// UI hasn't rendered.
export const getSuggestionStatusStorageKey = ({
  reviewId,
  doi,
  documentId,
}: StorageKeyArgs): string => {
  if (doi) {
    return `rejectedSuggestions-${reviewId}-${doi}`;
  }
  if (documentId) {
    return `rejectedSuggestions-${reviewId}-${documentId}`;
  }
  return `rejectedSuggestions-${reviewId}-no-document`;
};

export const getDE2SuggestionStatusStorageKey = ({
  reviewId,
  documentId,
}: {
  reviewId: string;
  documentId?: number;
}): string =>
  documentId === undefined
    ? `rejectedSuggestionsDE2-${reviewId}-no-document`
    : `rejectedSuggestionsDE2-${reviewId}-${documentId}`;

export const readSuggestionStatusFromBucket = (
  bucket: SuggestionStatusBucket | null | undefined,
  fieldName: string
): SuggestionStatus => {
  if (!bucket) return 'pending';

  const raw = bucket[fieldName];
  if (raw === 'accepted') return 'accepted';
  if (raw === 'rejected' || raw === true) return 'rejected';
  return 'pending';
};
